import { z } from 'zod';

/** The rows-per-page choices every paged table offers (APP-135). */
export const PAGE_SIZES = [25, 50, 100, 200] as const;
export const PageSize = z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)]);
export type PageSize = z.infer<typeof PageSize>;

/** Every paged table, by a stable id (also the key of the person's own page-size choice). */
export const TableId = z.enum([
  'audit_log',
  'people',
  'feedback',
  'sent_mail',
  'sync_runs',
  'directory_issues',
  'queue',
  'approvals',
  'tax_entry',
  'statements',
  'shareworks',
  'past_deadline',
  'payroll',
  'letters_to_hand_out',
  'overdue_elections',
  'my_rewards',
  'sent_awards',
  'spot_work',
  'spot_report',
  // APP-226: All Spot awards.
  'spot_all',
  'award_register',
  'heat_map',
  'team_people',
  'help_catalog',
  'import_rows',
  'my_feedback',
  'done_for_you',
]);
export type TableId = z.infer<typeof TableId>;

/** Each table's starting page size, until the person picks another. */
export const TABLE_PAGE_SIZE: Record<TableId, PageSize> = {
  audit_log: 50,
  people: 100,
  feedback: 25,
  sent_mail: 50,
  sync_runs: 25,
  directory_issues: 50,
  queue: 50,
  approvals: 25,
  tax_entry: 50,
  statements: 50,
  shareworks: 50,
  past_deadline: 25,
  payroll: 50,
  letters_to_hand_out: 25,
  overdue_elections: 25,
  my_rewards: 25,
  sent_awards: 25,
  spot_work: 25,
  spot_report: 25,
  spot_all: 50,
  award_register: 25,
  heat_map: 25,
  team_people: 25,
  help_catalog: 25,
  import_rows: 25,
  my_feedback: 25,
  done_for_you: 25,
};

/**
 * The highest page a request may ask for. An address asking for more reads as this one, and the
 * server sends the last page (APP-135).
 */
export const PAGE_MAX = 100_000;

/** Query fields for a paged list; the server falls back to the table's own size when `size` is absent. */
export const pageFields = {
  page: z.coerce.number().int().min(1).max(PAGE_MAX).default(1),
  size: z.coerce.number().int().min(1).max(200).optional(),
};

/** Where a page sits in the whole list, as the server resolved it. */
export const PageInfo = z
  .object({
    number: z.number().int().min(1),
    size: z.number().int().min(1).max(200),
    total: z.number().int().nonnegative(),
    pages: z.number().int().min(1),
  })
  .strict();
export type PageInfo = z.infer<typeof PageInfo>;

/** Clamp the requested page into 1..pages (a page past the end shows the last one). */
export function resolvePage(total: number, requested: number, size: number): PageInfo {
  const pages = Math.max(1, Math.ceil(total / size));
  return { number: Math.max(1, Math.min(requested, pages)), size, total, pages };
}

/** Rows to skip before this page. */
export function pageOffset(info: PageInfo): number {
  return (info.number - 1) * info.size;
}

/** The numbered buttons to show: first, last, and a window around the current page; null is "…". */
export function pageItems(current: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, null, pages];
  if (current >= pages - 3) return [1, null, pages - 4, pages - 3, pages - 2, pages - 1, pages];
  return [1, null, current - 1, current, current + 1, null, pages];
}

/** The 1-based page that holds the 0-based row, so a size change keeps the first row in view. */
export function pageHolding(firstRowIndex: number, size: number): number {
  return Math.floor(firstRowIndex / size) + 1;
}
